import React, { useEffect, useState } from 'react'
import { Popover, TextField } from '@mui/material'
import type { SxProps, TextFieldProps, Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import { toMomentLocaleForPicker } from './kedoLocale'

export const DATE_FORMAT = 'DD-MM-YYYY'
export const DATETIME_FORMAT = 'DD-MM-YYYY HH:mm'

type CalendarTextFieldProps = {
    value: string | Date | Moment | null | undefined
    onChange: (value: Moment | null) => void
    isDateTime?: boolean
    locale?: string
    disabled?: boolean
    error?: boolean
    fullWidth?: boolean
    size?: TextFieldProps['size']
    inputRef?: TextFieldProps['inputRef']
    placeholder?: string
    shouldDisableDate?: (date: Moment) => boolean
    preserveTimeOnCalendarChange?: boolean
    getCalendarTimeSource?: () => Moment
    sx?: SxProps<Theme>
    calendarSx?: SystemStyleObject<Theme>
}

const defaultCalendarSx: SystemStyleObject<Theme> = {
    width: 250,
    maxHeight: 260,
    '& .MuiPickersDay-root': {
        width: 28,
        height: 28,
        fontSize: '0.75rem',
    },
    '& .MuiDayCalendar-weekDayLabel': {
        width: 28,
        height: 28,
        fontSize: '0.7rem',
    },
}

export function CalendarTextField({
    value,
    onChange,
    isDateTime = false,
    locale,
    disabled = false,
    error = false,
    fullWidth = true,
    size = 'small',
    inputRef,
    placeholder = '',
    shouldDisableDate,
    preserveTimeOnCalendarChange = true,
    getCalendarTimeSource,
    sx,
    calendarSx,
}: CalendarTextFieldProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [inputValue, setInputValue] = useState('')
    const displayFormat = isDateTime ? DATETIME_FORMAT : DATE_FORMAT
    const parsedValue = value
        ? typeof value === 'string'
            ? moment(value, displayFormat, true)
            : moment(value)
        : null
    const selectedDate =
        parsedValue && parsedValue.isValid() ? parsedValue : moment()

    useEffect(() => {
        setInputValue(
            parsedValue && parsedValue.isValid()
                ? parsedValue.format(displayFormat)
                : ''
        )
    }, [displayFormat, value])

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value
        setInputValue(nextValue)

        if (!nextValue) {
            onChange(null)
            return
        }

        const parsedDate = moment(nextValue, displayFormat, true)
        if (!parsedDate.isValid()) {
            return
        }

        if (!isDateTime) {
            parsedDate.hours(0).minutes(0).seconds(0).milliseconds(0)
        } else {
            parsedDate.seconds(0).milliseconds(0)
        }

        onChange(parsedDate)
    }

    const handleCalendarChange = (date: Moment | null) => {
        if (!date || !date.isValid()) {
            return
        }

        const nextDate = date.clone()
        if (isDateTime && preserveTimeOnCalendarChange) {
            const timeSource =
                getCalendarTimeSource?.() ??
                (parsedValue && parsedValue.isValid() ? parsedValue : moment())
            nextDate
                .hours(timeSource.hours())
                .minutes(timeSource.minutes())
                .seconds(timeSource.seconds())
        } else {
            nextDate.hours(0).minutes(0).seconds(0)
        }
        nextDate.milliseconds(0)

        onChange(nextDate)
        setAnchorEl(null)
    }

    return (
        <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale={toMomentLocaleForPicker(locale)}
        >
            <TextField
                value={inputValue}
                onChange={handleInputChange}
                onClick={(event) => {
                    if (!disabled) {
                        setAnchorEl(event.currentTarget)
                    }
                }}
                disabled={disabled}
                size={size}
                error={error}
                fullWidth={fullWidth}
                inputRef={inputRef}
                inputProps={{ placeholder }}
                sx={sx}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <DateCalendar
                    value={selectedDate}
                    onChange={handleCalendarChange}
                    shouldDisableDate={shouldDisableDate}
                    sx={[
                        defaultCalendarSx,
                        ...(calendarSx ? [calendarSx] : []),
                    ]}
                />
            </Popover>
        </LocalizationProvider>
    )
}
